import api from "@/lib/api";
import type {
    CodeListItem,
    CodeType,
    ListCurrentUserCodesPageRequest,
} from "@openapi";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    CODE_LIST_PAGE_SIZE,
    type CodeListFilter,
    type CodeListWindow,
} from "./useCodeListWindow";

export interface CodeListPages extends CodeListWindow<CodeListItem> {
    /** True while the first page of the current filter is loading. */
    loading: boolean;
    /** Drops a deleted code without a round trip. */
    remove: (id: string) => void;
}

/** The server's query for a filter: one page starting `skip` rows in. */
export function pageRequest(
    { term = "", types = [], order = "descend" }: CodeListFilter,
    skip: number,
): ListCurrentUserCodesPageRequest {
    const search = term.trim();
    return {
        skip,
        limit: CODE_LIST_PAGE_SIZE,
        search: search || undefined,
        type: types.length ? (types as CodeType[]) : undefined,
        order: order === "ascend" ? "asc" : "desc",
    };
}

interface PagesState {
    /** The filter the rows belong to. */
    key: string;
    items: CodeListItem[];
    total: number;
    loading: boolean;
}

/**
 * The user's cloud codes a page at a time from GET /codes/page
 * (blocks-code/studio#249): the first page for the filter on each change
 * of it, the next page on "more". Nothing is read while `enabled` is
 * false (no signed-in user yet).
 */
export function useCodeListPages(
    filter: CodeListFilter,
    enabled: boolean,
    onError: (error: unknown) => void,
): CodeListPages {
    // The filter as primitives, so the key and the first page's request
    // change only when the filter does. Unlike the local list, the order
    // is part of the key: the server sorts before paging.
    const term = (filter.term ?? "").trim();
    const typesKey = [...(filter.types ?? [])].sort().join(",");
    const order = filter.order ?? "descend";
    const key = `${term}\u0000${typesKey}\u0000${order}`;
    const firstPage = useMemo(
        () =>
            pageRequest(
                { term, types: typesKey ? typesKey.split(",") : [], order },
                0,
            ),
        [term, typesKey, order],
    );

    const [state, setState] = useState<PagesState>({
        key,
        items: [],
        total: 0,
        loading: true,
    });
    // Rows of another filter are stale: shown as an empty, loading list
    // until the first page of this one arrives.
    const current: PagesState =
        state.key === key ? state : { key, items: [], total: 0, loading: true };

    // Read when an answer arrives: the error callback (a new one must not
    // restart the loading) and the number of the newest request, so that
    // an older answer, of a replaced filter or a superseded page, is dropped.
    const onErrorRef = useRef(onError);
    useEffect(() => {
        onErrorRef.current = onError;
    }, [onError]);
    const newest = useRef(0);
    const pending = useRef<number | null>(null);

    const load = useCallback(
        async (
            forKey: string,
            request: ListCurrentUserCodesPageRequest,
            append: boolean,
        ) => {
            const seq = ++newest.current;
            pending.current = seq;
            try {
                const page = await api.codes.listCurrentUserCodesPage(request);
                if (seq !== newest.current) return;
                setState((prev) => ({
                    key: forKey,
                    items:
                        append && prev.key === forKey
                            ? [...prev.items, ...page.items]
                            : page.items,
                    total: page.total,
                    loading: false,
                }));
            } catch (error) {
                if (seq !== newest.current) return;
                setState({ key: forKey, items: [], total: 0, loading: false });
                onErrorRef.current(error);
            } finally {
                if (pending.current === seq) pending.current = null;
            }
        },
        [],
    );

    useEffect(() => {
        if (!enabled) {
            // Whatever is in flight is for a user who is gone.
            newest.current++;
            pending.current = null;
            return;
        }
        const timeout = setTimeout(() => void load(key, firstPage, false), 0);
        return () => clearTimeout(timeout);
    }, [key, enabled, firstPage, load]);

    const loaded = enabled ? current.items.length : 0;
    const total = enabled ? current.total : 0;

    const showMore = useCallback(() => {
        if (!enabled || pending.current !== null || loaded >= total) return;
        void load(key, { ...firstPage, skip: loaded }, true);
    }, [enabled, loaded, total, key, firstPage, load]);

    const remove = useCallback((id: string) => {
        setState((prev) => {
            const items = prev.items.filter((code) => code.id !== id);
            return items.length === prev.items.length
                ? prev
                : { ...prev, items, total: Math.max(0, prev.total - 1) };
        });
    }, []);

    return {
        visible: enabled ? current.items : [],
        total,
        hasMore: loaded < total,
        showMore,
        loading: enabled && current.loading,
        remove,
    };
}
